import React, { useState, useContext, useEffect, useRef, useCallback } from 'react';
import './PaperSoccer.css';
import Footer from '../components/Footer';
import { LanguageContext } from '../translations/LanguageContext';

// New interactive game component
const InteractivePaperSoccerDemo = () => {
  const gridSize = 9; // Larger grid for better gameplay
  const [gameState, setGameState] = useState({
    ballPosition: { row: Math.floor(gridSize/2), col: Math.floor(gridSize/2) },
    currentPlayer: 1, // 1 or 2
    visitedLines: new Map(), // Changed from Set to Map to store player with each line
    visitedPoints: new Set([`${Math.floor(gridSize/2)}-${Math.floor(gridSize/2)}`]),
    gameOver: false,
    winner: null,
    validMoves: []
  });
  
  // Initialize valid moves on component mount and after each move
  useEffect(() => {
    if (!gameState.gameOver) {
      setGameState(prev => ({
        ...prev,
        validMoves: calculateValidMoves(prev.ballPosition, prev.visitedLines, gridSize)
      }));
    }
  }, [gameState.ballPosition, gameState.gameOver]);

  // Calculate valid moves from current position
  const calculateValidMoves = (position, visitedLines, gridSize) => {
    const { row, col } = position;
    const directions = [
      [-1, -1], [-1, 0], [-1, 1], 
      [0, -1],           [0, 1],
      [1, -1],  [1, 0],  [1, 1]
    ];
    
    return directions.filter(([dr, dc]) => {
      const newRow = row + dr;
      const newCol = col + dc;
      
      // Check if within grid bounds
      if (newRow < 0 || newRow >= gridSize || newCol < 0 || newCol >= gridSize) {
        return false;
      }
      
      // Check if line already visited
      const lineKey = `${row}-${col}-${newRow}-${newCol}`;
      const reversedLineKey = `${newRow}-${newCol}-${row}-${col}`;
      if (visitedLines.has(lineKey) || visitedLines.has(reversedLineKey)) {
        return false;
      }
      
      return true;
    }).map(([dr, dc]) => ({ row: row + dr, col: col + dc }));
  };
  
  // Process move when cell is clicked
  const handleCellClick = (row, col) => {
    if (gameState.gameOver) return;
    
    // Check if this is a valid move
    const isValidMove = gameState.validMoves.some(
      move => move.row === row && move.col === col
    );
    
    if (!isValidMove) return;
    
    // Update game state with the new move
    const { ballPosition, currentPlayer } = gameState;
    const lineKey = `${ballPosition.row}-${ballPosition.col}-${row}-${col}`;
    const pointKey = `${row}-${col}`;
    
    // Create a new map with all existing lines
    const newVisitedLines = new Map(gameState.visitedLines);
    // Store the current player with this line
    newVisitedLines.set(lineKey, currentPlayer);
    
    const newVisitedPoints = new Set(gameState.visitedPoints);
    const isPointAlreadyVisited = newVisitedPoints.has(pointKey);
    newVisitedPoints.add(pointKey);
    
    // Check for goal (win condition)
    const isGoal = (row === 0 || row === gridSize-1) && col === Math.floor(gridSize/2);
    
    // Determine if player gets an extra move (when landing on already visited point)
    const nextPlayer = isPointAlreadyVisited && !isGoal 
      ? currentPlayer 
      : currentPlayer === 1 ? 2 : 1;
    
    setGameState({
      ...gameState,
      ballPosition: { row, col },
      currentPlayer: nextPlayer,
      visitedLines: newVisitedLines,
      visitedPoints: newVisitedPoints,
      gameOver: isGoal,
      winner: isGoal ? currentPlayer : null
    });
  };
  
  // Reset game to initial state
  const resetGame = () => {
    setGameState({
      ballPosition: { row: Math.floor(gridSize/2), col: Math.floor(gridSize/2) },
      currentPlayer: 1,
      visitedLines: new Map(), // Changed from Set to Map
      visitedPoints: new Set([`${Math.floor(gridSize/2)}-${Math.floor(gridSize/2)}`]),
      gameOver: false,
      winner: null,
      validMoves: []
    });
  };
  
  // Render the game grid
  const renderGrid = () => {
    const cells = [];
    const centerRow = Math.floor(gridSize/2);
    const centerCol = Math.floor(gridSize/2);
    
    for (let i = 0; i < gridSize; i++) {
      for (let j = 0; j < gridSize; j++) {
        const isGoal = (i === 0 && j === centerCol) || 
                       (i === gridSize-1 && j === centerCol);
        const isBorder = i === 0 || i === gridSize-1 || j === 0 || j === gridSize-1;
        const isCurrentPosition = i === gameState.ballPosition.row && j === gameState.ballPosition.col;
        const isValidMove = gameState.validMoves.some(move => move.row === i && move.col === j);
        const isVisitedPoint = gameState.visitedPoints.has(`${i}-${j}`);
        const isStartPosition = i === centerRow && j === centerCol;
        
        let cellClass = 'ps-grid-cell';
        if (isGoal) cellClass += ' ps-goal-cell';
        if (isBorder) cellClass += ' ps-border-cell';
        if (isCurrentPosition) cellClass += ' ps-current-position';
        if (isValidMove) cellClass += ' ps-valid-move-cell';
        if (isVisitedPoint) cellClass += ' ps-visited-point';
        if (isStartPosition) cellClass += ' ps-start-cell';
        
        cells.push(
          <div 
            key={`${i}-${j}`} 
            className={cellClass}
            onClick={() => handleCellClick(i, j)}
          ></div>
        );
      }
    }
    
    // Render lines between visited points
    const lines = [];
    gameState.visitedLines.forEach((player, line) => {
      const [row1, col1, row2, col2] = line.split('-').map(Number);
      lines.push(
        <div 
          key={line} 
          className={`ps-line player-${player}`} // Use the player who made this move
          style={{
            position: 'absolute',
            left: `${col1 * (100/gridSize) + (100/gridSize/2)}%`,
            top: `${row1 * (100/gridSize) + (100/gridSize/2)}%`,
            width: `${Math.sqrt(Math.pow(col2-col1, 2) + Math.pow(row2-row1, 2)) * (100/gridSize)}%`,
            transform: `rotate(${Math.atan2(row2-row1, col2-col1)}rad)`,
            transformOrigin: 'left center'
          }}
        ></div>
      );
    });
    
    return (
      <div className="ps-interactive-game">
        <div className="ps-game-status">
          {gameState.gameOver 
            ? `Player ${gameState.winner} wins!` 
            : `Player ${gameState.currentPlayer}'s turn`}
        </div>
        <div className="ps-interactive-grid">
          {cells}
          {lines}
        </div>
        <button className="ps-reset-game" onClick={resetGame}>
          Reset Game
        </button>
      </div>
    );
  };
  
  return renderGrid();
};

// New auto-play game component
const AutoPlayPaperSoccerDemo = () => {
  const gridSize = 9; // Larger grid for better gameplay
  const [gameState, setGameState] = useState({
    ballPosition: { row: Math.floor(gridSize/2), col: Math.floor(gridSize/2) },
    currentPlayer: 1, // 1 or 2
    visitedLines: new Map(), // Changed from Set to Map to store player with each line
    visitedPoints: new Set([`${Math.floor(gridSize/2)}-${Math.floor(gridSize/2)}`]),
    gameOver: false,
    winner: null,
    validMoves: [],
    autoPlaying: true
  });
  
  const moveTimeoutRef = useRef(null);
  const resetTimeoutRef = useRef(null);
  
  // Calculate valid moves from current position
  const calculateValidMoves = (position, visitedLines, gridSize) => {
    const { row, col } = position;
    const directions = [
      [-1, -1], [-1, 0], [-1, 1], 
      [0, -1],           [0, 1],
      [1, -1],  [1, 0],  [1, 1]
    ];
    
    return directions.filter(([dr, dc]) => {
      const newRow = row + dr;
      const newCol = col + dc;
      
      // Check if within grid bounds
      if (newRow < 0 || newRow >= gridSize || newCol < 0 || newCol >= gridSize) {
        return false;
      }
      
      // Check if line already visited
      const lineKey = `${row}-${col}-${newRow}-${newCol}`;
      const reversedLineKey = `${newRow}-${newCol}-${row}-${col}`;
      if (visitedLines.has(lineKey) || visitedLines.has(reversedLineKey)) {
        return false;
      }
      
      return true;
    }).map(([dr, dc]) => ({ row: row + dr, col: col + dc }));
  };
  
  // Process move - memoize with useCallback
  const makeMove = useCallback((row, col) => {
    // Update game state with the new move
    setGameState((prevState) => {
      const { ballPosition, currentPlayer } = prevState;
      const lineKey = `${ballPosition.row}-${ballPosition.col}-${row}-${col}`;
      const pointKey = `${row}-${col}`;
      
      // Create a new map with all existing lines
      const newVisitedLines = new Map(prevState.visitedLines);
      // Store the current player with this line
      newVisitedLines.set(lineKey, currentPlayer);
      
      const newVisitedPoints = new Set(prevState.visitedPoints);
      const isPointAlreadyVisited = newVisitedPoints.has(pointKey);
      newVisitedPoints.add(pointKey);
      
      // Check for goal (win condition)
      const isGoal = (row === 0 || row === gridSize-1) && col === Math.floor(gridSize/2);
      
      // Determine if player gets an extra move (when landing on already visited point)
      const nextPlayer = isPointAlreadyVisited && !isGoal 
        ? currentPlayer 
        : currentPlayer === 1 ? 2 : 1;
      
      return {
        ...prevState,
        ballPosition: { row, col },
        currentPlayer: nextPlayer,
        visitedLines: newVisitedLines,
        visitedPoints: newVisitedPoints,
        gameOver: isGoal,
        winner: isGoal ? currentPlayer : null
      };
    });
  }, [gridSize]);
  
  // Reset game to initial state
  const resetGame = () => {
    setGameState({
      ballPosition: { row: Math.floor(gridSize/2), col: Math.floor(gridSize/2) },
      currentPlayer: 1,
      visitedLines: new Map(),
      visitedPoints: new Set([`${Math.floor(gridSize/2)}-${Math.floor(gridSize/2)}`]),
      gameOver: false,
      winner: null,
      validMoves: [],
      autoPlaying: true
    });
  };
  
  // Select a move for AI with goal-seeking behavior - memoize with useCallback
  const selectAIMove = useCallback((validMoves) => {
    if (validMoves.length === 0) return null;
    
    // Define target goal based on player - remove unused variable
    const centerCol = Math.floor(gridSize/2);
    
    // Score each move based on how it helps reach the target goal
    return gameState.validMoves.length > 0 ? validMoves.map(move => {
      let score = 0;
      
      // Vertical progress toward goal (primary objective)
      if (gameState.currentPlayer === 1) {
        // Player 1 wants to go down
        score += (move.row - gameState.ballPosition.row) * 3; // Prioritize downward movement
      } else {
        // Player 2 wants to go up
        score += (gameState.ballPosition.row - move.row) * 3; // Prioritize upward movement
      }
      
      // Horizontal progress toward center (secondary objective)
      score -= Math.abs(move.col - centerCol);
      
      // Add some randomness
      score += Math.random() * 2;
      
      return { move, score };
    })
    .sort((a, b) => b.score - a.score)[0].move : null;
  }, [gameState, gridSize]);
  
  // Initialize valid moves on component mount and after each move
  useEffect(() => {
    if (!gameState.gameOver) {
      const validMoves = calculateValidMoves(gameState.ballPosition, gameState.visitedLines, gridSize);
      
      if (validMoves.length === 0) {
        // Player is stuck with no moves - other player wins
        setGameState(prev => ({
          ...prev,
          gameOver: true,
          winner: prev.currentPlayer === 1 ? 2 : 1,
          validMoves: []
        }));
      } else {
        setGameState(prev => ({
          ...prev,
          validMoves: validMoves
        }));
      }
    }
  }, [gameState.ballPosition, gameState.gameOver, gameState.visitedLines]);
  
  // Auto-play logic
  useEffect(() => {
    // Clear any existing timeouts
    if (moveTimeoutRef.current) clearTimeout(moveTimeoutRef.current);
    if (resetTimeoutRef.current) clearTimeout(resetTimeoutRef.current);
    
    if (gameState.gameOver) {
      // Reset the game after 4 seconds when game is over
      resetTimeoutRef.current = setTimeout(() => {
        resetGame();
      }, 4000);
    } else if (gameState.autoPlaying && gameState.validMoves.length > 0) {
      // Make an AI move after a short delay
      moveTimeoutRef.current = setTimeout(() => {
        const aiMove = selectAIMove(gameState.validMoves);
        if (aiMove) {
          makeMove(aiMove.row, aiMove.col);
        }
      }, 1000); // Changed from 1500ms to 1000ms
    }
    
    // Cleanup on unmount or before next effect run
    return () => {
      if (moveTimeoutRef.current) clearTimeout(moveTimeoutRef.current);
      if (resetTimeoutRef.current) clearTimeout(resetTimeoutRef.current);
    };
  }, [gameState.validMoves, gameState.gameOver, gameState.autoPlaying, gameState.currentPlayer, makeMove, selectAIMove]);
  
  // Render the game grid (similar to interactive but without click handlers)
  const renderGrid = () => {
    const cells = [];
    const centerRow = Math.floor(gridSize/2);
    const centerCol = Math.floor(gridSize/2);
    
    for (let i = 0; i < gridSize; i++) {
      for (let j = 0; j < gridSize; j++) {
        const isGoal = (i === 0 && j === centerCol) || 
                       (i === gridSize-1 && j === centerCol);
        const isBorder = i === 0 || i === gridSize-1 || j === 0 || j === gridSize-1;
        const isCurrentPosition = i === gameState.ballPosition.row && j === gameState.ballPosition.col;
        const isVisitedPoint = gameState.visitedPoints.has(`${i}-${j}`);
        const isStartPosition = i === centerRow && j === centerCol;
        
        let cellClass = 'ps-grid-cell';
        if (isGoal) cellClass += ' ps-goal-cell';
        if (isBorder) cellClass += ' ps-border-cell';
        if (isCurrentPosition) cellClass += ' ps-current-position';
        if (isVisitedPoint) cellClass += ' ps-visited-point';
        if (isStartPosition) cellClass += ' ps-start-cell';
        
        cells.push(
          <div 
            key={`${i}-${j}`} 
            className={cellClass}
          ></div>
        );
      }
    }
    
    // Render lines between visited points
    const lines = [];
    gameState.visitedLines.forEach((player, line) => {
      const [row1, col1, row2, col2] = line.split('-').map(Number);
      lines.push(
        <div 
          key={line} 
          className={`ps-line player-${player}`} // Use the player who made this move
          style={{
            position: 'absolute',
            left: `${col1 * (100/gridSize) + (100/gridSize/2)}%`,
            top: `${row1 * (100/gridSize) + (100/gridSize/2)}%`,
            width: `${Math.sqrt(Math.pow(col2-col1, 2) + Math.pow(row2-row1, 2)) * (100/gridSize)}%`,
            transform: `rotate(${Math.atan2(row2-row1, col2-col1)}rad)`,
            transformOrigin: 'left center'
          }}
        ></div>
      );
    });
    
    return (
      <div className="ps-interactive-game">
        <div className="ps-game-status">
          {gameState.gameOver 
            ? (gameState.validMoves.length === 0 && !gameState.winner 
                ? `Player ${gameState.currentPlayer} is stuck! Player ${gameState.currentPlayer === 1 ? 2 : 1} wins!` 
                : `Player ${gameState.winner} wins!`)
            : `Player ${gameState.currentPlayer}'s turn`}
        </div>
        <div className="ps-interactive-grid ps-auto-play-grid">
          {cells}
          {lines}
        </div>
        <button className="ps-reset-game" onClick={resetGame}>
          Reset Game
        </button>
      </div>
    );
  };
  
  return renderGrid();
};

function PaperSoccer() {
  const [activeTab, setActiveTab] = useState('overview');
  const { language, translations } = useContext(LanguageContext);
  const t = translations[language].paperSoccerPage;

  return (
    <div className="paper-soccer-page">
      {/* Hero Section */}
      <header className="ps-hero">
        <div className="ps-hero-content">
          <h1>{t.heroTitle}</h1>
          <p className="ps-hero-subtitle">{t.heroSubtitle}</p>
          <div className="ps-tech-badge">
            <span>{t.techBadge}</span>
          </div>
        </div>
        <div className="ps-hero-image-container">
          <img 
            src="/PaperSoccer/phone-mockup.png" 
            alt={t.heroImageAlt} 
            className="ps-hero-image"
          />
        </div>
      </header>

      {/* Tabs Section */}
      <nav className="ps-tabs">
        <button 
          className={activeTab === 'overview' ? 'active' : ''} 
          onClick={() => setActiveTab('overview')}
        >
          {t.tabOverview}
        </button>
        <button 
          className={activeTab === 'howToPlay' ? 'active' : ''} 
          onClick={() => setActiveTab('howToPlay')}
        >
          {t.tabHowToPlay}
        </button>
        <button 
          className={activeTab === 'rules' ? 'active' : ''} 
          onClick={() => setActiveTab('rules')}
        >
          {t.tabRules}
        </button>
      </nav>

      {/* Main Content */}
      <section className="ps-content-section">
        {activeTab === 'overview' && (
          <div className="ps-overview">
            <div className="ps-content-row">
              <div className="ps-content-text">
                <h2>{t.overviewTitle}</h2>
                <p>{t.overviewDescription}</p>
                
                <h3>{t.featuresTitle}</h3>
                <ul className="ps-features-list">
                  {t.featuresList.map((feature, index) => (
                    <li key={index}>{feature}</li>
                  ))}
                </ul>
              </div>
              <div className="ps-content-visual">
                {/* Replace interactive game with auto-play version */}
                <AutoPlayPaperSoccerDemo />
              </div>
            </div>
            
            <div className="ps-customization">
              <h3>{t.customizationTitle}</h3>
              <div className="ps-customization-options">
                <div className="ps-customization-text">
                  <div className="ps-color-option">
                    <div className="ps-color-circle ps-color-red"></div>
                    <div className="ps-color-circle ps-color-blue"></div>
                    <div className="ps-color-circle ps-color-green"></div>
                    <div className="ps-color-circle ps-color-yellow"></div>
                  </div>
                  <p>{t.customizationDescription}</p>
                </div>
                <div className="ps-customization-mockup">
                  <img 
                    src="/PaperSoccer/customize.png" 
                    alt="Player customization interface" 
                    className="ps-customize-image"
                    loading="lazy"
                  />
                </div>
              </div>
            </div>
            
          </div>
        )}

        {activeTab === 'howToPlay' && (
          <div className="ps-how-to-play">
            <h2>{t.howToPlayTitle}</h2>
            <div className="ps-steps">
              {t.howToPlaySteps.map((step, index) => (
                <div key={index} className="ps-step-card">
                  <div className="ps-step-number">{index + 1}</div>
                  <p>{step}</p>
                </div>
              ))}
            </div>
            
            {/* Replace the single gameplay image with three instructive GIFs */}
            <div className="ps-gameplay-tutorials">
              <div className="ps-tutorial-section">
                <h3>{t.basicMovesTitle || "Basic Moves"}</h3>
                <div className="ps-gameplay-gif-container">
                  <img 
                    src="/PaperSoccer/gifStep.gif" 
                    alt="Basic movement demonstration" 
                    className="ps-gameplay-gif"
                    loading="lazy"
                  />
                  <p>{t.basicMovesDescription || "Move from point to point in eight possible directions."}</p>
                </div>
              </div>

              <div className="ps-tutorial-section">
                <h3>{t.extraMovesTitle || "Extra Moves"}</h3>
                <div className="ps-gameplay-gif-container">
                  <img 
                    src="/PaperSoccer/gifExtraStep.gif" 
                    alt="Extra move demonstration" 
                    className="ps-gameplay-gif"
                    loading="lazy"
                  />
                  <p>{t.extraMovesDescription || "When you reach a previously visited point, you get an extra move!"}</p>
                </div>
              </div>

              <div className="ps-tutorial-section">
                <h3>{t.winningTitle || "Winning the Game"}</h3>
                <div className="ps-gameplay-gif-container">
                  <img 
                    src="/PaperSoccer/gifWinning.gif" 
                    alt="Winning move demonstration" 
                    className="ps-gameplay-gif"
                    loading="lazy"
                  />
                  <p>{t.winningDescription || "Score a goal by moving your ball into the opponent's goal area."}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'rules' && (
          <div className="ps-rules">
            <h2>{t.rulesTitle}</h2>
            
            <div className="ps-content-row">
              <div className="ps-content-text">
                {/* Move existing rules categories here */}
                {t.rulesCategories.map((category, index) => (
                  <div key={index} className="ps-rules-category">
                    <h3>{category.title}</h3>
                    <ul>
                      {category.rules.map((rule, ruleIndex) => (
                        <li key={ruleIndex}>{rule}</li>
                      ))}
                    </ul>
                  </div>
                ))}
                
                {/* Removed ps-rules-illustration div with valid/invalid move examples */}
              </div>
              
              {/* Interactive game component with removed heading */}
              <div className="ps-content-visual">
                <InteractivePaperSoccerDemo />
              </div>
            </div>
          </div>
        )}
      </section>

      <section className="ps-download-section">
        <h2>{t.downloadTitle}</h2>
        <div className="ps-store-buttons">
          {/* Replace anchor tag with button */}
          <button className="ps-store-button" aria-label="Download from App Store">
            <img src="/PaperSoccer/app-store-badge.png" alt="App Store" />
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default PaperSoccer;
